import React, { useState, useCallback } from 'react';
import { Shield, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { CONTACT_SECURITY_CONFIG } from '../config/contact-security';

interface ContactVerificationProps {
  type: 'phone' | 'email';
  value: string;
  children: React.ReactNode;
  className?: string;
  onVerified?: () => void;
}

interface VerificationResult {
  userInteraction: boolean;
  browserChecks: boolean;
  timeBased: boolean;
  behaviorAnalysis: boolean;
}

export function ContactVerification({ 
  type, 
  value, 
  children, 
  className = '',
  onVerified 
}: ContactVerificationProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [verificationStep, setVerificationStep] = useState<'idle' | 'verifying' | 'success' | 'failed'>('idle');
  const [verificationProgress, setVerificationProgress] = useState(0);
  const [verificationResults, setVerificationResults] = useState<VerificationResult>({
    userInteraction: false,
    browserChecks: false,
    timeBased: false,
    behaviorAnalysis: false
  });

  // Verificaciones anti-bot
  const performUserInteractionCheck = useCallback((): boolean => {
    // Verificar si ha habido interacción previa del usuario
    let hasInteraction = false;
    
    const checkInteraction = () => {
      hasInteraction = true;
      document.removeEventListener('mousemove', checkInteraction);
      document.removeEventListener('click', checkInteraction);
      document.removeEventListener('scroll', checkInteraction);
      document.removeEventListener('keydown', checkInteraction);
    };

    document.addEventListener('mousemove', checkInteraction);
    document.addEventListener('click', checkInteraction);
    document.addEventListener('scroll', checkInteraction);
    document.addEventListener('keydown', checkInteraction);

    // Simular pequeña interacción para activar el listener
    setTimeout(() => {
      if (!hasInteraction) {
        hasInteraction = Math.random() > CONTACT_SECURITY_CONFIG.VERIFICATIONS.USER_INTERACTION.PASS_PROBABILITY;
      }
    }, CONTACT_SECURITY_CONFIG.UI.DELAYS.MODAL_DISPLAY);

    return hasInteraction;
  }, []);

  const performBrowserChecks = useCallback((): boolean => {
    // Verificaciones del navegador
    const checks = [
      // Verificar si JavaScript está habilitado
      typeof window !== 'undefined',
      // Verificar si hay plugins
      navigator.plugins && navigator.plugins.length > 0,
      // Verificar user agent
      navigator.userAgent && navigator.userAgent.length > CONTACT_SECURITY_CONFIG.VERIFICATIONS.BROWSER_CHECKS.MIN_USER_AGENT_LENGTH,
      // Verificar si las cookies están habilitadas
      navigator.cookieEnabled,
      // Verificar resolución de pantalla
      screen.width > CONTACT_SECURITY_CONFIG.VERIFICATIONS.BROWSER_CHECKS.MIN_SCREEN_WIDTH && 
      screen.height > CONTACT_SECURITY_CONFIG.VERIFICATIONS.BROWSER_CHECKS.MIN_SCREEN_HEIGHT,
      // Verificar timezone
      Intl && Intl.DateTimeFormat,
      // Verificar si es un navegador headless
      !navigator.webdriver,
      // Verificar si hay características del navegador
      'ontouchstart' in window || 'onmousemove' in window
    ];

    const passedChecks = checks.filter(Boolean).length;
    return passedChecks >= CONTACT_SECURITY_CONFIG.VERIFICATIONS.BROWSER_CHECKS.MIN_PASSED_CHECKS;
  }, []);

  const performTimeBasedCheck = useCallback((): boolean => {
    // Verificación basada en tiempo - simular análisis de comportamiento
    // Simular análisis de patrones de tiempo
    const isHuman = Math.random() > CONTACT_SECURITY_CONFIG.VERIFICATIONS.TIME_BASED.PASS_PROBABILITY;
    return isHuman;
  }, []);

  const performBehaviorAnalysis = useCallback((): boolean => {
    // Análisis de comportamiento del usuario
    const behaviors = [
      // Verificar si el usuario ha estado en la página por un tiempo mínimo
      performance.now() > CONTACT_SECURITY_CONFIG.VERIFICATIONS.BEHAVIOR_ANALYSIS.MIN_PAGE_TIME,
      // Verificar si hay eventos de mouse
      'onmousemove' in window,
      // Verificar si hay eventos de teclado
      'onkeydown' in window,
      // Verificar si hay eventos de scroll
      'onscroll' in window
    ];

    const passedBehaviors = behaviors.filter(Boolean).length;
    return passedBehaviors >= CONTACT_SECURITY_CONFIG.VERIFICATIONS.BEHAVIOR_ANALYSIS.MIN_PASSED_BEHAVIORS;
  }, []);

  const startVerification = useCallback(() => {
    setIsModalOpen(true);
    setVerificationStep('verifying');
    setVerificationProgress(0);

    // Ejecutar verificaciones
    const results = [
      performUserInteractionCheck(),
      performBrowserChecks(),
      performTimeBasedCheck(),
      performBehaviorAnalysis()
    ];

    const verificationResults: VerificationResult = {
      userInteraction: results[0],
      browserChecks: results[1],
      timeBased: results[2],
      behaviorAnalysis: results[3]
    };

    setVerificationResults(verificationResults);

    // Calcular score final
    const passedChecks = Object.values(verificationResults).filter(Boolean).length;
    const finalScore = (passedChecks / CONTACT_SECURITY_CONFIG.VERIFICATIONS.BROWSER_CHECKS.TOTAL_CHECKS) * 100;

    setVerificationProgress(100);

    if (finalScore >= CONTACT_SECURITY_CONFIG.SECURITY.MIN_VERIFICATION_SCORE) {
      setVerificationStep('success');
      setTimeout(() => {
        handleSuccess();
      }, CONTACT_SECURITY_CONFIG.UI.DELAYS.SUCCESS_DISPLAY);
    } else {
      setVerificationStep('failed');
    }
  }, [performUserInteractionCheck, performBrowserChecks, performTimeBasedCheck, performBehaviorAnalysis]);

  const handleSuccess = () => {
    setIsModalOpen(false);
    setVerificationStep('idle');
    setVerificationProgress(0);
    
    if (onVerified) {
      onVerified();
    }

    // Redirigir según el tipo de contacto
    if (type === 'phone') {
      window.location.href = `tel:${value}`;
    } else if (type === 'email') {
      window.location.href = `mailto:${value}`;
    }
  };

  const handleRetry = () => {
    setVerificationStep('idle');
    setVerificationProgress(0);
    startVerification();
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setVerificationStep('idle');
    setVerificationProgress(0);
  };

  return (
    <>
      <div onClick={startVerification} className={className}>
        {children}
      </div>

      {/* Modal de Verificación */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Shield className="h-6 w-6 text-neuro-blue" />
                <h3 className="text-lg font-semibold text-gray-900">
                  {CONTACT_SECURITY_CONFIG.UI.MESSAGES.VERIFICATION_TITLE}
                </h3>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Cerrar verificación"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            {verificationStep === 'verifying' && (
              <div className="space-y-4">
                <p className="text-gray-600 text-sm">
                  {CONTACT_SECURITY_CONFIG.UI.MESSAGES.VERIFICATION_DESCRIPTION}
                </p>
                
                {/* Barra de progreso */}
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-neuro-blue h-2 rounded-full transition-all duration-300"
                    style={{ width: `${verificationProgress}%` }}
                  />
                </div>

                {/* Verificaciones individuales */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${verificationResults.userInteraction ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <span>Verificación de interacción</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${verificationResults.browserChecks ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <span>Verificación del navegador</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${verificationResults.timeBased ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <span>Análisis de comportamiento</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${verificationResults.behaviorAnalysis ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <span>Verificación de patrones</span>
                  </div>
                </div>
              </div>
            )}

            {verificationStep === 'success' && (
              <div className="text-center space-y-4">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
                <h4 className="text-lg font-semibold text-gray-900">
                  {CONTACT_SECURITY_CONFIG.UI.MESSAGES.SUCCESS_TITLE}
                </h4>
                <p className="text-gray-600 text-sm">
                  {type === 'phone' ? CONTACT_SECURITY_CONFIG.UI.MESSAGES.SUCCESS_PHONE : CONTACT_SECURITY_CONFIG.UI.MESSAGES.SUCCESS_EMAIL}
                </p>
              </div>
            )}

            {verificationStep === 'failed' && (
              <div className="text-center space-y-4">
                <AlertCircle className="h-16 w-16 text-red-500 mx-auto" />
                <h4 className="text-lg font-semibold text-gray-900">
                  {CONTACT_SECURITY_CONFIG.UI.MESSAGES.FAILED_TITLE}
                </h4>
                <p className="text-gray-600 text-sm">
                  {CONTACT_SECURITY_CONFIG.UI.MESSAGES.FAILED_DESCRIPTION}
                </p>
                <button
                  onClick={handleRetry}
                  className={CONTACT_SECURITY_CONFIG.UI.STYLES.RETRY_BUTTON}
                >
                  {CONTACT_SECURITY_CONFIG.UI.MESSAGES.RETRY_BUTTON}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
